import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaServer, FaDatabase, FaProjectDiagram, FaCodeBranch, FaCheckCircle, FaGithub, FaLayerGroup, FaWhatsapp, FaShieldAlt } from 'react-icons/fa';
import styles from './CaseStudyModal.module.css'; // Reusing identical styles from Resumia!

const DocFlowModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className={styles.overlay} onClick={onClose}>
          <motion.div 
            className={styles.modalContent}
            initial={{ y: 50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={styles.closeBtn} onClick={onClose}>
              <FaTimes />
            </button>

            <div className={styles.modalHeader}>
              <span className={styles.badge}><FaLayerGroup/> SaaS Architecture</span>
              <h2>Doc-Flow — Complete Implementation Plan</h2>
              <p>B2B SaaS platform for financial consultants for KYC verification via WhatsApp automation.</p>
            </div>

            <div className={styles.scrollableArea}>
              
              {/* Architecture Flow */}
              <section className={styles.section}>
                <h3><FaProjectDiagram /> System Architecture (C4 Model)</h3>
                <div className={styles.flowContainer}>
                  <div className={styles.flowBox}><FaServer style={{ marginRight: '8px' }}/> Consultant (React UI)</div>
                  <div className={styles.flowArrow}>↓</div>
                  <div className={styles.flowBox}><FaServer style={{ marginRight: '8px' }}/> Express API <br/><small>Auth, Client Mgmt, File Upload, Socket.IO</small></div>
                  <div className={styles.flowArrow}>↓</div>
                  <div className={styles.flowGrid}>
                    <div className={styles.flowAgent}><FaDatabase style={{ marginRight: '8px' }}/> MongoDB Atlas<br/><small>Multi-Tenant DB</small></div>
                    <div className={styles.flowAgent}><FaServer style={{ marginRight: '8px' }}/> Cloudinary<br/><small>Secure File Storage</small></div>
                    <div className={styles.flowAgent}><FaWhatsapp style={{ marginRight: '8px' }}/> Baileys Protocol<br/><small>WhatsApp Session Isolation</small></div>
                  </div>
                  <div className={styles.flowArrow}>↑↓</div>
                  <div className={styles.flowBox}><FaServer style={{ marginRight: '8px' }}/> Client (Any Device)<br/><small>Accesses secure doc requests link</small></div>
                </div>
              </section>

              {/* Advanced System Features */}
              <section className={styles.section}>
                <h3><FaShieldAlt /> Advanced Engineering Strategies</h3>
                <div className={styles.agentGrid}>
                  
                  <div className={styles.agentCard}>
                    <div className={styles.agentIcon}><FaDatabase/></div>
                    <h4>Multi-Tenancy Design</h4>
                    <p>Shared Database, Shared Schema with strict logical isolation.</p>
                    <ul>
                      <li><FaCheckCircle className={styles.checkIcon}/> Every doc filtered by consultantId</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> Scalable cost structure</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> Zero cross-tenant data leaks</li>
                    </ul>
                  </div>

                  <div className={styles.agentCard}>
                    <div className={styles.agentIcon}><FaWhatsapp/></div>
                    <h4>WhatsApp Session Isolation</h4>
                    <p>Maintains persistent multi-device sockets securely.</p>
                    <ul>
                      <li><FaCheckCircle className={styles.checkIcon}/> Auth keys in MongoDB</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> Composite index isolation</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> In-memory socket Maps</li>
                    </ul>
                  </div>

                  <div className={styles.agentCard}>
                    <div className={styles.agentIcon}><FaServer/></div>
                    <h4>Real-time Infrastructure</h4>
                    <p>Live synchronized state between consultant and backend.</p>
                    <ul>
                      <li><FaCheckCircle className={styles.checkIcon}/> Socket.IO Events</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> Live QR code delivery</li>
                      <li><FaCheckCircle className={styles.checkIcon}/> File upload notifications</li>
                    </ul>
                  </div>

                </div>
              </section>

              {/* Implementation Roadmap */}
              <section className={styles.section}>
                <h3><FaProjectDiagram /> Implementation Roadmap</h3>
                <div className={styles.ragBox}>
                  <p style={{marginBottom: "1rem"}}>Scaling from a robust prototype to an Enterprise-grade SaaS platform.</p>
                  <div className={styles.ragFlow}>
                    <span>Phase 1: Foundation (Rate limit, Validation)</span> ➔ <span>Phase 2: SaaS Core (Stripe, Tiers, Emails)</span> ➔ <span>Phase 3: Growth (Webhooks, Admin)</span>
                  </div>
                </div>
              </section>

              {/* Stack */}
              <section className={styles.section}>
                <h3><FaCodeBranch /> Technology Stack</h3>
                <div className={styles.techStack}>
                  <span className={styles.techTag}>React SPA</span>
                  <span className={styles.techTag}>Node.js Express</span>
                  <span className={styles.techTag}>MongoDB Atlas</span>
                  <span className={styles.techTag}>Socket.IO</span>
                  <span className={styles.techTag}>Baileys WA Library</span>
                  <span className={styles.techTag}>Cloudinary</span>
                  <span className={styles.techTag}>Stripe API</span>
                </div>
              </section>

              <div className={styles.modalFooter}>
                <a href="https://github.com/princedesai012/Doc-Flow.git" target="_blank" rel="noreferrer" className={styles.footerBtn}>
                  <FaGithub /> View Repository
                </a>
              </div>

            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DocFlowModal;
